import { parseSurfaceGeometry } from '../../shared/surface-geometry';
import {resolveFt3dZoneVisual} from '../../shared/ft3d-zone-visual';
// encounter-map/ft3d/persistent-zones.ts — project the encounter blob's PERSISTED zone anchors for
// the FT3D iframe.
// ------------------------------------------------------------------------------------------------
// Owner report 2026-09-09 ("FT3D pAOE effects do not show outline of aoe"): the placement preview is
// torn down on cast and nothing in the FT3D runtime drew the zone the cast left behind. The legacy XZ
// widget had XZPersistentAOELayer.collectPersistentStatuses() for this; the 3D runtime had no consumer.
//
// This is the parent-side half: a PURE projection of (encounter blob) → Ft3dPersistentZoneProjection[]
// in the placement contract's axes (feet; y = floor-plane depth, z = elevation). The iframe seats each
// row with the SAME renderer the placement preview uses (preview3d setCampaignPersistentZones), so a
// persisted zone can never disagree with the outline it was placed with.
//
// Zone identity mirrors the server (persistent_aoe_status_is_zone_anchor) and the legacy layer:
//   - a STAMPED anchor (status_aoe_source=1) on the encounter, a creature or an inventory row IS a zone;
//   - a source-less row renders only when its def is aoe_follows_target AND no stamped anchor shares its
//     status_connected_id (otherwise it is a rider/occupant row on a zone target, not the zone);
//   - status_private rows, campaign-paused rows and a hidden trap's zone (non-GM viewer) are skipped.

import {
  activeStatuses,
  entityId,
  itemIdOf,
  numberOf,
  position,
  privateRow,
  recordOf,
  itemAppliesObscuredStatus,
  ruleLayerValue,
  statusDefinition,
  statusField,
  stringOf,
  truthyPhp,
} from './perception';
import { parseFt3dZoneVisual, resolveFt3dPaintedWallVisual, type ClassAuraTwinkleTeam } from '../../shared/ft3d-zone-visual';

/** The class-aura twinkle visual (gold ally / violet foe), through the same parser the iframe trusts. */
function classAuraTwinkleVisual(team: ClassAuraTwinkleTeam, shape: unknown) {
  return parseFt3dZoneVisual({ schema: 'ft.vfx.zone.v1', style: 'class-aura-twinkle', team }, shape);
}
import { wallLength } from '../../shared/painted-wall-geometry';
import {parsePaintedWallSectionSnapshot} from '../../shared/painted-wall-section-state';
import {
  isFt3dAoeShape,
  type Ft3dAoePoint,
  type Ft3dAoeShape,
  type Ft3dPersistentZoneProjection,
} from '../../shared/ft3d-aoe-placement-contract';

type Row = Record<string, unknown>;

const CENTERED_SHAPES = new Set<Ft3dAoeShape>(['sphere', 'circle', 'cylinder', 'cube', 'square']);

function aoeTypeEntry(id: string): { variable_name: string; ground_only?: boolean } | null {
  if (!id || typeof AOETypes === 'undefined') return null;
  const wanted = id.toUpperCase();
  const entry = AOETypes.find(type => String(type.item_id ?? '').toUpperCase() === wanted);
  return entry ? { variable_name: String(entry.variable_name ?? ''), ground_only: entry.ground_only === true } : null;
}

function definitionLabel(definition: Row | null): string {
  if (!definition) return '';
  const items = recordOf(definition.items);
  return stringOf(items?.item_name ?? definition.item_name).trim();
}

function statusPointCoordinate(status: Row, key: string, fallback: number): number {
  const raw = statusField(status, key);
  return raw === null || raw === undefined || raw === '' ? fallback : numberOf(raw, fallback);
}

/** The parent floors an UNAUTHORED height/depth at 1 (FT3DAOEPlacement rawHeightFt/rawDepthFt). */
function dimensionFt(status: Row, definition: Row | null, statusKey: string, layerKey: string, encounterArr: Row): number {
  const placed = numberOf(statusField(status, statusKey));
  if (placed > 0) return placed;
  return Math.max(1, numberOf(ruleLayerValue(definition, layerKey, encounterArr)) || 1);
}

function viewerIsGm(): boolean {
  const fateTide = (window as Window & { FateTide?: { user?: { isGM?: boolean } } }).FateTide;
  return fateTide?.user?.isGM === true;
}

function hiddenInventoryIds(encounterArr: Row): Set<string> {
  const hidden = new Set<string>();
  const check = (window as Window & { checkTargetHidden?: (obj: unknown) => boolean }).checkTargetHidden;
  if (typeof check !== 'function') return hidden;
  for (const candidate of Array.isArray(encounterArr.encounter_inventories_arr) ? encounterArr.encounter_inventories_arr : []) {
    const row = recordOf(candidate);
    if (!row) continue;
    let isHidden = false;
    try { isHidden = check(row) === true; } catch { isHidden = false; }
    if (isHidden) hidden.add(entityId(row, 'inventory').toUpperCase());
  }
  return hidden;
}

/**
 * Every renderable persisted zone in the blob, deduped by status id. Pure: reads the blob and the
 * AOETypes catalog only. Follows-target zones carry the stored geometry as a fallback; the iframe
 * re-seats them on the attached placement's LIVE position every frame.
 */
export function collectFt3dPersistentZones(rawEncounterArr: unknown): Ft3dPersistentZoneProjection[] {
  const encounterArr = recordOf(rawEncounterArr);
  if (!encounterArr) return [];
  const gm = viewerIsGm();
  const hiddenTrapCasters = gm ? new Set<string>() : hiddenInventoryIds(encounterArr);
  const inCombat = truthyPhp(encounterArr.in_combat ?? recordOf(encounterArr.encounters)?.in_combat);

  type Entry = { status: Row; attachedId: string | null; attachedKind: 'creature' | 'inventory' | null; attachedRow: Row | null };
  const entries: Entry[] = activeStatuses(encounterArr).map(status => ({ status, attachedId: null, attachedKind: null, attachedRow: null }));
  for (const kind of ['creature', 'inventory'] as const) {
    const listKey = kind === 'creature' ? 'encounter_creatures_arr' : 'encounter_inventories_arr';
    const rows = Array.isArray(encounterArr[listKey]) ? encounterArr[listKey] as unknown[] : [];
    for (const candidate of rows) {
      const row = recordOf(candidate);
      if (!row) continue;
      if (kind === 'inventory' && numberOf(row.inventory_deleted ?? recordOf(row.inventory)?.inventory_deleted) === 1) continue;
      const id = entityId(row, kind).toUpperCase();
      if (!id) continue;
      for (const status of activeStatuses(row)) {
        entries.push({ status, attachedId: id, attachedKind: kind, attachedRow: row });
      }
    }
  }

  const stampedConnected = new Set<string>();
  for (const { status } of entries) {
    if (numberOf(statusField(status, 'status_aoe_source')) !== 1) continue;
    const connected = stringOf(statusField(status, 'status_connected_id')).trim().toUpperCase();
    if (connected) stampedConnected.add(connected);
  }

  const zones = new Map<string, Ft3dPersistentZoneProjection>();
  for (const { status, attachedId, attachedKind, attachedRow } of entries) {
    if (numberOf(statusField(status, 'status_private')) !== 0) continue;
    const definition = statusDefinition(status, encounterArr);
    if (!definition || !truthyPhp(ruleLayerValue(definition, 'persistent_aoe', encounterArr))) continue;
    const stamped = numberOf(statusField(status, 'status_aoe_source')) === 1;
    const followsTarget = truthyPhp(ruleLayerValue(definition, 'aoe_follows_target', encounterArr));
    // CLASS AURA STANCE (owner 2026-09-24, plans/class-aura-stance-rework-2026-09-24.md §3.5): an always-on class
    // aura is drawn ONLY while the encounter is in combat — out of combat the mechanics keep running server-side
    // but nothing renders, so a party walking around does not carry permanent rings. The gate is the encounter's
    // own in_combat field; never the hostname.
    const classAura = followsTarget && truthyPhp(ruleLayerValue(definition, 'class_aura', encounterArr));
    if (classAura && !inCombat) continue;
    const connectedId = stringOf(statusField(status, 'status_connected_id')).trim().toUpperCase();
    if (!stamped && (!attachedId || !followsTarget || (!!connectedId && stampedConnected.has(connectedId)))) continue;
    const statusId = stringOf(statusField(status, 'status_id') ?? statusField(status, 'status_connected_id')).trim().toUpperCase();
    if (!statusId || zones.has(statusId)) continue;
    const casterId = stringOf(statusField(status, 'status_caster_id')).trim().toUpperCase();
    if (casterId && hiddenTrapCasters.has(casterId)) continue;
    // A private carrier hides its own aura from players exactly like the carrier itself.
    if (attachedRow && attachedKind && !gm && privateRow(attachedRow, attachedKind)) continue;

    const typeId = stringOf(statusField(status, 'status_aoe_type')).trim()
      || itemIdOf(ruleLayerValue(definition, 'aoe_type', encounterArr));
    const typeEntry = aoeTypeEntry(typeId);
    const shape = String(typeEntry?.variable_name ?? '').toLowerCase();
    if (!isFt3dAoeShape(shape)) continue;

    let distanceFt = numberOf(statusField(status, 'status_aoe_distance'));
    if (!(distanceFt > 0)) distanceFt = numberOf(ruleLayerValue(definition, 'aoe_distance', encounterArr));
    if (!(distanceFt > 0)) distanceFt = 5;
    const heightFt = shape === 'cube' ? distanceFt
      : dimensionFt(status, definition, 'status_aoe_distance_height', 'aoe_distance_height', encounterArr);
    const depthFt = shape === 'cube' ? distanceFt
      : dimensionFt(status, definition, 'status_aoe_distance_depth', 'aoe_distance_depth', encounterArr);

    // Stored geometry (feet). A lean blob drops numeric-zero keys — missing means zero, the codebase-
    // wide convention (persistent_aoe_geometry.php collect_persistent_aoes).
    const originX = statusPointCoordinate(status, 'status_origin_point_x', 0);
    const originY = statusPointCoordinate(status, 'status_origin_point_y', 0);
    const originZ = statusPointCoordinate(status, 'status_origin_point_z', 0);
    let origin: Ft3dAoePoint = { x: originX, y: originY, z: originZ };
    // The distance point follows the same missing-means-zero rule PER COMPONENT once any component
    // is present: a wall to (30, 0) arrives with distance_y stripped, and defaulting it to the
    // origin's y would swing the wall (Codex review 2026-09-09). A row with no distance component at
    // all (never placed) degenerates to its origin.
    const hasDistance = ['status_distance_point_x', 'status_distance_point_y', 'status_distance_point_z']
      .some(key => { const raw = statusField(status, key); return raw !== null && raw !== undefined && raw !== ''; });
    let endpoint: Ft3dAoePoint = hasDistance
      ? {
        x: statusPointCoordinate(status, 'status_distance_point_x', 0),
        y: statusPointCoordinate(status, 'status_distance_point_y', 0),
        z: statusPointCoordinate(status, 'status_distance_point_z', 0),
      }
      : { ...origin };
    const centered = CENTERED_SHAPES.has(shape);
    // A follows-target zone rides its carrier: keep the STORED span's shape (a rotated box's axis, a
    // directional zone's segment) and translate its midpoint onto the carrier's blob position, so
    // the row is drawable before the iframe re-seats it on the live placement. A row with no span
    // of its own gets a synthetic x-aligned span sized from the ability, centred on the carrier.
    if (followsTarget && attachedRow) {
      const at = position(attachedRow);
      const live: Ft3dAoePoint = { x: at.x, y: at.y ?? 0, z: at.z };
      const degenerate = Math.hypot(endpoint.x - origin.x, endpoint.y - origin.y, endpoint.z - origin.z) <= 1e-6;
      if (degenerate) {
        const spanHalf = centered ? (shape === 'cube' || shape === 'square' ? distanceFt / 2 : distanceFt) : 0;
        origin = { x: live.x - spanHalf, y: live.y, z: live.z };
        endpoint = { x: live.x + spanHalf, y: live.y, z: live.z };
      } else {
        const dx = live.x - (origin.x + endpoint.x) / 2;
        const dy = live.y - (origin.y + endpoint.y) / 2;
        const dz = live.z - (origin.z + endpoint.z) / 2;
        origin = { x: origin.x + dx, y: origin.y + dy, z: origin.z + dz };
        endpoint = { x: endpoint.x + dx, y: endpoint.y + dy, z: endpoint.z + dz };
      }
    }
    const geometryValue = statusField(status, 'status_aoe_geometry');
    const wallSnapshot=parsePaintedWallSectionSnapshot(geometryValue);
    const wallGeometry = wallSnapshot?.geometry;
    if (geometryValue != null && !wallGeometry && !parseSurfaceGeometry(geometryValue)) continue;
    if (wallGeometry) { origin = wallGeometry.points[0]!; endpoint = wallGeometry.points[wallGeometry.points.length - 1]!; }
    const center: Ft3dAoePoint = {
      x: (origin.x + endpoint.x) / 2,
      y: (origin.y + endpoint.y) / 2,
      z: (origin.z + endpoint.z) / 2,
    };
    const axisX = endpoint.x - origin.x;
    const axisY = endpoint.y - origin.y;
    // A rotated square/cube persists origin→endpoint as its local x-axis (buildFt3dAoeTargetSelection).
    const rotationDeg = (shape === 'cube' || shape === 'square') && Math.hypot(axisX, axisY) > 1e-6
      ? Math.atan2(axisY, axisX) * 180 / Math.PI
      : 0;

    const visual = classAura
      ? classAuraTwinkleVisual(truthyPhp(ruleLayerValue(definition, 'effect_only_affects_opposing_team_type', encounterArr)) ? 'foe' : 'ally', shape)
      : wallGeometry ? resolveFt3dPaintedWallVisual(definition,ruleLayerValue(definition,'damage_type',encounterArr))
      : resolveFt3dZoneVisual(definition,shape,ruleLayerValue(definition,'damage_type',encounterArr),itemAppliesObscuredStatus(definition));
    zones.set(statusId, {
      abilityId: stringOf(statusField(status, 'status_origin_id') || statusField(status, 'status_item_id')).trim().toUpperCase(),
      ...(visual ? {visual} : {}),
      ...(wallGeometry ? { wallGeometry } : {}),
      ...(wallSnapshot?.sections?{wallSections:wallSnapshot.sections}:{}),
      statusId,
      connectedId,
      casterId,
      label: definitionLabel(definition) || 'Zone',
      shape,
      distanceFt: wallGeometry ? wallLength(wallGeometry.points) : distanceFt,
      heightFt,
      depthFt,
      groundOnly: typeEntry?.ground_only === true || shape === 'circle' || shape === 'square',
      followsTarget,
      attachedEntityId: followsTarget ? attachedId : null,
      attachedKind: followsTarget ? attachedKind : null,
      origin,
      endpoint,
      center,
      rotationDeg,
    });
  }
  return [...zones.values()];
}
